import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import nocobaseService from '../services/nocobaseService';
import InsightCard from './InsightCard';
import ViewAllArticlesCard from './ViewAllArticlesCard';

export default function MasonryCardsSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch latest 5 posts from NocoDB
  useEffect(() => {
    const fetchLatestPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get all published posts
        const allPosts = await nocobaseService.getPosts({ status: 'Published' });
        
        // Sort by date (newest first)
        const sortedPosts = allPosts.sort((a, b) => {
          const dateA = new Date(a.Date || a.published_at || a.date || 0);
          const dateB = new Date(b.Date || b.published_at || b.date || 0);
          return dateB - dateA; // Newest first
        });
        
        // Get latest 5 posts
        const latestPosts = sortedPosts.slice(0, 5);
        
        console.log('📰 Latest 5 posts for homepage:', latestPosts.map(p => ({
          id: p.id,
          name: p.Name,
          date: p.Date || p.published_at || p.date
        })));
        
        setInsights(latestPosts);
        console.log('✅ Insights loaded:', {
          total: latestPosts.length,
          insights: latestPosts.map(p => ({ id: p.id, name: p.Name }))
        });
        setLoading(false);
      } catch (err) {
        console.error('❌ Failed to fetch latest posts:', err);
        setError(err);
        setInsights([]);
        setLoading(false);
      }
    };

    fetchLatestPosts();
  }, []);

  const refetch = () => {
    const fetchLatestPosts = async () => {
      try {
        setLoading(true);
        setError(null);
        const allPosts = await nocobaseService.getPosts({ status: 'Published' });
        const sortedPosts = allPosts.sort((a, b) => {
          const dateA = new Date(a.Date || a.published_at || a.date || 0);
          const dateB = new Date(b.Date || b.published_at || b.date || 0);
          return dateB - dateA;
        });
        const latestPosts = sortedPosts.slice(0, 5);
        setInsights(latestPosts);
      } catch (err) {
        console.error('Failed to refetch posts:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLatestPosts();
  };

  // State for hover effects
  const [_hoveredCard, setHoveredCard] = useState(null);

  // Handle card hover effects
  const handleCardHover = (cardId) => (e) => {
    setHoveredCard(cardId);
    
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.2)';
    
    // Show overlay
    const overlay = e.currentTarget.querySelector('.card-overlay');
    if (overlay) overlay.style.opacity = '1';
    
    // Hide original title
    const title = e.currentTarget.querySelector('.card-title');
    if (title) {
      title.style.opacity = '0';
    }
    
    // Show description with animation
    const description = e.currentTarget.querySelector('.card-description');
    if (description) {
      description.style.transform = 'translateY(0)';
      description.style.opacity = '1';
    }
  };

  const handleCardLeave = (cardId) => (e) => {
    setHoveredCard(null);
    
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
    
    // Hide overlay
    const overlay = e.currentTarget.querySelector('.card-overlay');
    if (overlay) overlay.style.opacity = '0';
    
    // Show original title
    const title = e.currentTarget.querySelector('.card-title');
    if (title) {
      title.style.opacity = '1';
    }
    
    // Hide description
    const description = e.currentTarget.querySelector('.card-description');
    if (description) {
      description.style.transform = 'translateY(20px)';
      description.style.opacity = '0';
    }
  };

  // Handle card click - use ID for guaranteed uniqueness
  const handleCardClick = (insight) => {
    const insightId = insight.id;
    const url = `/${currentLanguage}/insights/id/${insightId}`
    console.log('🔗 Homepage insight click:', {
      name: insight.Name,
      id: insightId,
      targetUrl: url
    });
    navigate(url);
    
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 100);
  };

  // Only show the section if there are insights (after loading is complete)
  console.log('🎯 Rendering MasonryCardsSection:', { 
    loading, 
    insightsCount: insights?.length || 0, 
    hasError: !!error,
    insights: insights,
    isEmpty: !insights || insights.length === 0,
    shouldShow: !loading && insights && insights.length > 0
  });
  
  // Don't show section if loading or if there are no insights
  if (loading) {
    return null; // Don't show anything while loading
  }
  
  // Don't show section if there are no insights
  if (!insights || insights.length === 0) {
    return null; // Hide section completely when empty
  }
  
  return (
    <>
      <section
        className="section section--full-width section--bottom-block-white space-bottom crop-x"
        style={{
          display: "block",
          padding: "clamp(40px, 8vh, 80px) 0px",
          margin: "0px",
          backgroundPosition: "100% 100%",
          width: "100%",
          position: "relative",
          background: "transparent",
          backgroundSize: "initial",
          backgroundRepeat: "initial",
          backgroundColor: "transparent",
          maxWidth: "100%",
          overflowX: "hidden",
          paddingBottom: "clamp(200px, 25vh, 300px)",
          zIndex: 5,
        }}
      >
        {/* Video Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 1
        }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: 0.3
            }}
          >
            <source src="/videos/bg2.mp4" type="video/mp4" />
          </video>
        </div>
        <div
          className="container container--large"
          style={{
            width: "100%",
            boxSizing: "border-box",
            maxWidth: "clamp(320px, 90vw, 1200px)",
            margin: "0px auto",
            position: "relative",
            zIndex: 10,
            padding: "0 clamp(15px, 4vw, 20px)"
          }}
        >
          <h2
            className="promoted"
            style={{
              fontFamily: "ff-real-text-pro, sans-serif",
              fontStyle: "normal",
              fontWeight: 300,
              letterSpacing: "-0.025em",
              lineHeight: "1.3em",
              marginLeft: "0px",
              marginTop: "0px",
              marginBottom: "80px",
              fontSize: "clamp(32px, 5vw, 48px)",
              color: "#000000",
              textAlign: "center",
              width: "100%",
              padding: "0 20px"
            }}
          >
            {t('insights.title')}
          </h2>
          <p style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "clamp(16px, 2.5vw, 20px)",
            color: "#666666",
            textAlign: "center",
            width: "100%",
            padding: "10px 20px 0",
            maxWidth: "800px",
            margin: "0 auto",
            lineHeight: "1.6"
          }}>
            {t('insights.subtitle')}
          </p>
          
          {/* Error Message */}
          {error && (
            <div style={{
              textAlign: "center",
              padding: "20px",
              margin: "20px 0",
              background: "rgba(239, 68, 68, 0.1)",
              borderRadius: "12px",
              border: "1px solid rgba(239, 68, 68, 0.2)"
            }}>
              <p style={{
                fontSize: "16px",
                color: "#dc2626",
                fontFamily: "Inter, sans-serif",
                marginBottom: "10px"
              }}>
                {currentLanguage === 'ar' 
                  ? 'حدث خطأ في تحميل المقالات' 
                  : 'Failed to load insights'}
              </p>
              <button 
                onClick={refetch}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {currentLanguage === 'ar' ? 'إعادة المحاولة' : 'Retry'}
              </button>
            </div>
          )}
          
          <div className="ais-Hits">
            <div
              className="masonry-cards promoted-items"
              style={{
                boxSizing: "border-box",
                color: "rgb(255, 255, 255)",
                padding: "30px 0px 0px",
                margin: "0px",
                position: "relative",
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "15px",
                alignItems: "stretch",
                width: "100%",
              }}
            >
              {/* Dynamic Insight Cards */}
              {insights.map((insight, index) => (
                <InsightCard
                  key={`${insight.id}-${index}`}
                  insight={insight}
                  onMouseEnter={handleCardHover(insight.id)}
                  onMouseLeave={handleCardLeave(insight.id)}
                  onClick={handleCardClick}
                />
              ))}
              
              {/* View All Articles Card */}
              <ViewAllArticlesCard
                key="view-all-articles"
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              />
            </div>
          </div>
        </div>
      </section>
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        /* Mobile-specific adjustments for Insights Cards */
        @media (max-width: 768px) {
          /* Section Container Mobile */
          section {
            padding: clamp(30px, 6vh, 50px) 0px !important;
            padding-bottom: clamp(50px, 10vh, 80px) !important;
          }
          
          /* Mobile Grid Layout - 1 column */
          .masonry-cards {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 20px !important;
            padding: 20px 15px !important;
            margin: 0px !important;
            max-width: 100% !important;
            width: 100% !important;
            overflow: visible !important;
          }
          
          /* Mobile Cards - Organized Layout */
          .masonry-cards article {
            height: 280px !important;
            min-height: 280px !important;
            margin: 0px !important;
            width: 100% !important;
            max-width: 100% !important;
            border-radius: 16px !important;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
            overflow: hidden !important;
            display: block !important;
          }
          
          /* Mobile Card - Better Text Positioning */
          .masonry-cards article .card-title {
            bottom: 15px !important;
            left: 15px !important;
            right: 15px !important;
            top: auto !important;
            height: auto !important;
            background: none !important;
            padding: 0 !important;
          }
          
          /* Mobile Card - Better Tag Layout */
          .masonry-cards article .card-title > div:last-child {
            gap: 6px !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
          }
          
          /* Mobile Card Tags - Smaller Size */
          .masonry-cards article .card-title > div:last-child > div {
            padding: 4px 8px !important;
            font-size: 8px !important;
            border-radius: 8px !important;
          }
          
          /* Hover Overlay Mobile - Descriptions Only */
          .card-overlay {
            padding: 20px !important;
            opacity: 0 !important;
            background: linear-gradient(135deg, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.95)) !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
            text-align: center !important;
          }
          
          /* Show overlay on touch/tap for mobile */
          .masonry-cards article:active .card-overlay {
            opacity: 1 !important;
          }
          
          /* Horizontal Card Text Mobile */
          .card-overlay h3 {
            font-size: clamp(16px, 4vw, 20px) !important;
            line-height: 1.3 !important;
            margin: 0px 0px 12px 0px !important;
            text-align: center !important;
            font-weight: 700 !important;
          }
          
          .card-overlay p:last-child {
            font-size: clamp(12px, 3vw, 14px) !important;
            line-height: 1.5 !important;
            margin: 0px !important;
            text-align: center !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 4 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
          }
          
          .card-overlay p:first-child {
            font-size: 10px !important;
            margin-bottom: 8px !important;
            text-align: center !important;
            opacity: 0.9 !important;
          }
          
          /* Section Title Mobile */
          .promoted {
            margin-left: 0px !important;
            margin-top: 0px !important;
            margin-bottom: 40px !important;
            font-size: clamp(32px, 7vw, 40px) !important;
            text-align: center !important;
            padding: 0 20px !important;
            width: 100% !important;
          }
          
          /* Container Mobile */
          .container {
            padding: 0 15px !important;
            max-width: 100% !important;
          }
          
          /* Disable hover effects on mobile */
          .masonry-cards article:hover {
            transform: none !important;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
          }
        }
        
        /* Tablet adjustments */
        @media (min-width: 769px) and (max-width: 1024px) {
          .masonry-cards {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 18px !important;
            padding: 20px 0px 0px !important;
          }
          
          .masonry-cards article {
            min-height: 300px !important;
            height: 300px !important;
          }
          
          .masonry-cards article .card-title {
            bottom: 16px !important;
            left: 16px !important;
            right: 16px !important;
          }
        }
        
        /* Small mobile devices */
        @media (max-width: 480px) {
          .masonry-cards {
            padding: 15px 10px !important;
            gap: 15px !important;
          }
          
          .masonry-cards article {
            height: 250px !important;
            min-height: 250px !important;
            border-radius: 12px !important;
          }
          
          .masonry-cards article .card-title h3 {
            font-size: 14px !important;
            line-height: 1.3 !important;
          }
          
          .masonry-cards article .card-title > div:last-child > div {
            padding: 3px 6px !important;
            font-size: 7px !important;
          }
        }
      `}</style>
      
      <style
        dangerouslySetInnerHTML={{
          __html: `
html {
  line-height: 1.15;
  text-size-adjust: 100%;
}

body {
  margin: 0px;
  font-family: ff-real-text-pro, sans-serif;
  font-size: 16px;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
}

.dot--white::before {
  content: "•";
  color: rgba(0, 0, 0, 0.4);
  margin: 0 8px;
}
`,
        }}
      />
    </>
  );
}